/**
 * Per-player stroke signature: fit amplitude / timing bands from a rally,
 * filter false positives, tag topspin and bad placement.
 *
 * Keep in sync with Backend/src/utils/playerStrokeSignature.js and
 * pose_worker/pose_core/heuristics/player_signature.py.
 */

const MIN_RALLY = 3;
const BAD_PLACEMENT_ABS_DEG = -30;
const MISSING_TRUNK_ROTATION_TAG = "missing_trunk_rotation";
const BAND_MARGIN_DEG = 8;
const BAND_PCT = 0.1;
const ISI_ISOLATED_FACTOR = 3.25;
const PHASE_HI_DROP = 3;
const PHASE_LO_DROP = 0.25;
const RHYTHM_PEAK_FRAC = 0.5;
const RHYTHM_AMP_FRAC = 0.5;
const RHYTHM_LONG_RALLY_MIN = 10;
const RHYTHM_LONG_PEAK_FRAC = 0.42;
const RHYTHM_LONG_AMP_FRAC = 0.35;
const RHYTHM_MED_RALLY_MIN = 6;
const RHYTHM_MED_PEAK_FRAC = 0.46;
const RHYTHM_MED_AMP_FRAC = 0.38;

const PHASE_KEYS = [
  "preparation",
  "forward_swing",
  "contact",
  "follow_through",
  "recovery",
];

function median(nums) {
  const a = (nums || []).filter((n) => Number.isFinite(n)).sort((x, y) => x - y);
  if (!a.length) return null;
  const mid = Math.floor(a.length / 2);
  return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
}

function robustBand(values, { minHalf = 8, pct = BAND_PCT, dropWiggles = false, dropHigh = false } = {}) {
  const xs = (values || []).filter((n) => Number.isFinite(n));
  const med = median(xs);
  if (med == null) return null;
  const mad = median(xs.map((x) => Math.abs(x - med))) || 0;
  let used = xs;
  if (dropWiggles && med > 0) {
    const inliers = used.filter((x) => x >= med * RHYTHM_PEAK_FRAC);
    if (inliers.length >= 2) used = inliers;
  }
  if (dropHigh) {
    const half = Math.max(minHalf, 2 * mad, 0.18 * Math.abs(med));
    const inliers = used.filter((x) => x <= med + half);
    if (inliers.length >= 2) used = inliers;
  }
  const mn = Math.min(...used);
  const mx = Math.max(...used);
  const padLo = Math.max(Math.abs(mn) * pct, (mx - mn) * pct, minHalf * pct);
  const padHi = Math.max(Math.abs(mx) * pct, (mx - mn) * pct, minHalf * pct);
  return {
    n: xs.length,
    med: Number(med.toFixed(4)),
    mad: Number(mad.toFixed(4)),
    min: Number(mn.toFixed(4)),
    max: Number(mx.toFixed(4)),
    lo: Number((mn - padLo).toFixed(4)),
    hi: Number((mx + padHi).toFixed(4)),
  };
}

function phaseDurationMs(stroke, key) {
  const r = stroke?.phases?.[key];
  const t0 = Number(r?.t_start_ms);
  const t1 = Number(r?.t_end_ms);
  if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 < t0) return null;
  return t1 - t0;
}

function contactTime(s) {
  const t = Number(s?.t_contact_ms);
  if (Number.isFinite(t)) return t;
  return Number(s?.t_peak_ms) || 0;
}

function strokeTags(s) {
  return Array.isArray(s?.tags) ? s.tags.filter(Boolean) : [];
}

function hasTag(s, tag) {
  return strokeTags(s).includes(tag);
}

function withTags(s, tags) {
  const next = [...new Set(tags.filter(Boolean))];
  return { ...s, tags: next };
}

function simpleClusters(strokes, gapMs) {
  const sorted = [...(strokes || [])].sort(
    (a, b) => contactTime(a) - contactTime(b)
  );
  if (!sorted.length) return [];
  const gap =
    Number.isFinite(gapMs) && gapMs > 0
      ? gapMs
      : Math.max(400, Math.round((median(sorted.slice(1).map((s, i) => contactTime(s) - contactTime(sorted[i]))) || 800) * 1.25));
  const clusters = [];
  let cur = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    if (contactTime(sorted[i]) - contactTime(sorted[i - 1]) <= gap) {
      cur.push(sorted[i]);
    } else {
      clusters.push(cur);
      cur = [sorted[i]];
    }
  }
  clusters.push(cur);
  return clusters;
}

function isiFromPool(pool) {
  const sorted = [...(pool || [])].sort((a, b) => contactTime(a) - contactTime(b));
  const gaps = [];
  for (let i = 1; i < sorted.length; i += 1) {
    const g = contactTime(sorted[i]) - contactTime(sorted[i - 1]);
    if (g > 0) gaps.push(g);
  }
  return { gaps, T: median(gaps) };
}

/**
 * Fit personal gesture bands from the longest rally (≥3), else all strokes.
 * First-stroke prep duration is excluded from the prep timing band.
 */
export function fitPlayerStrokeSignature(strokes, opts = {}) {
  const list = Array.isArray(strokes) ? strokes : [];
  const clusters = Array.isArray(opts.clusters) ? opts.clusters : simpleClusters(list);
  const rallies = clusters.filter((c) => c.length >= MIN_RALLY);
  let pool = [];
  let source = "global";
  if (rallies.length) {
    pool = rallies.reduce((a, b) => (b.length > a.length ? b : a));
    source = "rally";
  } else if (list.length >= MIN_RALLY) {
    pool = list;
    source = "all";
  } else {
    return null;
  }

  const prepAngles = pool
    .map((s) => Number(s.valley_angle_deg))
    .filter((n) => Number.isFinite(n));
  const peaks = pool
    .map((s) => Number(s.angle_deg))
    .filter((n) => Number.isFinite(n));
  const dxs = pool
    .map((s) => Number(s.shoulder_dx))
    .filter((n) => Number.isFinite(n));
  const amps = pool
    .map((s) => Number(s.ang_rise))
    .filter((n) => Number.isFinite(n));

  const phase_ms = {};
  for (const key of PHASE_KEYS) {
    const vals = [];
    pool.forEach((s, i) => {
      if (key === "preparation" && i === 0) return;
      const d = phaseDurationMs(s, key);
      if (Number.isFinite(d)) vals.push(d);
    });
    const band = robustBand(vals, { minHalf: 40 });
    if (band) phase_ms[key] = band;
  }

  const { T, gaps } = isiFromPool(pool);
  const isi_ms = robustBand(gaps, { minHalf: 80 });
  if (isi_ms && T != null) isi_ms.med = Number(T.toFixed(1));

  return {
    source,
    n: pool.length,
    prep_angle: robustBand(prepAngles, { minHalf: 8 }),
    peak: robustBand(peaks, { minHalf: 12, dropWiggles: true }),
    dx: robustBand(dxs, { minHalf: 0.005, dropHigh: true }),
    amplitude: robustBand(amps, { minHalf: 8, dropWiggles: true }),
    phase_ms,
    isi_ms,
    calibrated_from: opts.calibratedFrom || "clip",
  };
}

export function payloadFromSignature(sig, meta = {}) {
  if (!sig || typeof sig !== "object") return null;
  const strokeKind = String(meta.strokeKind || "");
  const payload = {
    prep_angle: sig.prep_angle || null,
    peak: sig.peak || null,
    dx: sig.dx || null,
    amplitude: sig.amplitude || null,
    phase_ms: sig.phase_ms || {},
    isi_ms: sig.isi_ms || null,
    calibrated_from_te_id: meta.teId || sig.calibrated_from_te_id || null,
    job_id: meta.jobId || sig.job_id || null,
    heuristics_version: meta.heuristicsVersion || sig.heuristics_version || null,
    source: sig.source || null,
    n: sig.n || null,
  };
  const isTopspinKind =
    strokeKind === "forehand_topspin" || strokeKind === "backhand_topspin";
  if (isTopspinKind) {
    payload.topspin_peak_min =
      sig.peak?.lo ?? sig.peak?.med ?? null;
    payload.topspin_amp_min =
      sig.amplitude?.lo ?? sig.amplitude?.med ?? null;
  }
  return payload;
}

export function topspinGateFromThresholds(sessionBase, sessionTopspin) {
  const tsPeak = Number(
    sessionTopspin?.topspin_peak_min ?? sessionTopspin?.peak?.lo ?? sessionTopspin?.peak?.med
  );
  const tsAmp = Number(
    sessionTopspin?.topspin_amp_min ??
      sessionTopspin?.amplitude?.lo ??
      sessionTopspin?.amplitude?.med
  );
  if (Number.isFinite(tsPeak) && Number.isFinite(tsAmp)) {
    return {
      peakMin: tsPeak,
      ampMin: tsAmp,
      source: "warmup_topspin",
    };
  }
  const fhHi = Number(sessionBase?.peak?.hi);
  const ampHi = Number(sessionBase?.amplitude?.hi);
  if (Number.isFinite(fhHi) && Number.isFinite(ampHi)) {
    return { peakMin: fhHi, ampMin: ampHi, source: "upper_fh" };
  }
  return null;
}

function phaseOk(stroke, sig, { skipPrep } = {}) {
  const bands = sig?.phase_ms;
  if (!bands || typeof bands !== "object") return true;
  for (const key of PHASE_KEYS) {
    if (key === "preparation" && skipPrep) continue;
    const band = bands[key];
    if (!band) continue;
    const d = phaseDurationMs(stroke, key);
    if (!Number.isFinite(d)) continue;
    const lo = Number(band.lo);
    const hi = Number(band.hi);
    if (Number.isFinite(hi) && d > hi * PHASE_HI_DROP) return false;
    if (Number.isFinite(lo) && lo > 0 && d < lo * PHASE_LO_DROP) return false;
  }
  return true;
}

function peakFloor(sig) {
  const lo = Number(sig?.peak?.lo);
  if (!Number.isFinite(lo)) return null;
  return lo - Math.max(Math.abs(lo) * BAND_PCT, BAND_MARGIN_DEG * BAND_PCT);
}

function ampFloor(sig) {
  const lo = Number(sig?.amplitude?.lo);
  if (!Number.isFinite(lo)) return null;
  return lo - Math.max(Math.abs(lo) * BAND_PCT, 1);
}

function longestClusterSize(clusters) {
  if (!Array.isArray(clusters) || !clusters.length) return 0;
  return clusters.reduce((max, c) => Math.max(max, Array.isArray(c) ? c.length : 0), 0);
}

function rhythmBandFracs(rallyLen = 0) {
  const len = Number(rallyLen) || 0;
  if (len >= RHYTHM_LONG_RALLY_MIN) {
    return { peakFrac: RHYTHM_LONG_PEAK_FRAC, ampFrac: RHYTHM_LONG_AMP_FRAC };
  }
  if (len >= RHYTHM_MED_RALLY_MIN) {
    return { peakFrac: RHYTHM_MED_PEAK_FRAC, ampFrac: RHYTHM_MED_AMP_FRAC };
  }
  return { peakFrac: RHYTHM_PEAK_FRAC, ampFrac: RHYTHM_AMP_FRAC };
}

function inPersonalBand(stroke, sig, { onRhythm, peakFrac, ampFrac } = {}) {
  if (!sig) return true;
  const peak = Number(stroke?.angle_deg);
  const amp = Number(stroke?.ang_rise);
  const peakMed = Number(sig.peak?.med);
  const ampMed = Number(sig.amplitude?.med);
  const pFrac = Number(peakFrac) > 0 ? Number(peakFrac) : RHYTHM_PEAK_FRAC;
  const aFrac = Number(ampFrac) > 0 ? Number(ampFrac) : RHYTHM_AMP_FRAC;
  if (onRhythm) {
    if (Number.isFinite(amp) && Number.isFinite(ampMed) && amp < ampMed * aFrac) {
      return false;
    }
    // Signed shoulder-open can peak below 0° in sustained rallies; trust ang_rise.
    if (
      Number.isFinite(peak) &&
      peak > 0 &&
      Number.isFinite(peakMed) &&
      peakMed > 0 &&
      peak < peakMed * pFrac
    ) {
      return false;
    }
    return true;
  }
  const pLo = peakFloor(sig);
  const aLo = ampFloor(sig);
  if (Number.isFinite(peak) && Number.isFinite(pLo) && peak < pLo) return false;
  if (Number.isFinite(amp) && Number.isFinite(aLo) && amp < aLo) return false;
  return true;
}

function filterByIsiAndTiming(strokes, sig, opts = {}) {
  const list = Array.isArray(strokes) ? strokes : [];
  if (!list.length) return [];
  const { peakFrac, ampFrac } = rhythmBandFracs(
    longestClusterSize(opts.clusters)
  );
  const sorted = [...list].sort((a, b) => contactTime(a) - contactTime(b));
  const isiMed = Number(sig?.isi_ms?.med);
  const isiHi = Number.isFinite(isiMed)
    ? Math.max(isiMed * ISI_ISOLATED_FACTOR, Number(sig?.isi_ms?.hi) || 0)
    : null;
  const kept = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const t = contactTime(sorted[i]);
    const prev = i > 0 ? contactTime(sorted[i - 1]) : null;
    const next = i < sorted.length - 1 ? contactTime(sorted[i + 1]) : null;
    const gapPrev = prev != null ? t - prev : null;
    const gapNext = next != null ? next - t : null;
    const nearPrev = isiHi != null && gapPrev != null && gapPrev <= isiHi;
    const nearNext = isiHi != null && gapNext != null && gapNext <= isiHi;
    const isolated = !nearPrev && !nearNext;
    if (sorted.length >= MIN_RALLY && Number.isFinite(isiMed) && isolated) {
      continue;
    }
    const onRhythm = nearPrev || nearNext;
    if (!phaseOk(sorted[i], sig, { skipPrep: onRhythm })) continue;
    if (!inPersonalBand(sorted[i], sig, {
      onRhythm,
      peakFrac,
      ampFrac,
    })) {
      continue;
    }
    kept.push(sorted[i]);
  }
  return kept;
}

function tagStroke(stroke, sig, gate, { warmupFamilyTag } = {}) {
  const tags = [...strokeTags(stroke)];
  const valley = Number(stroke?.valley_angle_deg);
  const peak = Number(stroke?.angle_deg);
  const amp = Number(stroke?.ang_rise);
  const prepLo = Number(sig?.prep_angle?.lo);

  if (Number.isFinite(valley)) {
    const extraPivot =
      valley <= BAD_PLACEMENT_ABS_DEG ||
      (Number.isFinite(prepLo) && valley < prepLo - BAND_MARGIN_DEG);
    if (extraPivot && !tags.includes("bad_placement")) tags.push("bad_placement");
  }

  let topspin = false;
  if (gate) {
    topspin =
      Number.isFinite(peak) &&
      Number.isFinite(amp) &&
      peak >= gate.peakMin &&
      amp >= gate.ampMin;
  }
  if (!topspin && stroke?.topspin_prep_cue) topspin = true;
  if (!topspin && stroke?.topspin_table_cue) topspin = true;
  if (topspin && !tags.includes("topspin")) tags.push("topspin");
  // Topspin warmup: plain FH/BH still count, but keep the family tag.
  else if (
    warmupFamilyTag &&
    !tags.includes(warmupFamilyTag) &&
    !tags.includes("topspin")
  ) {
    tags.push(warmupFamilyTag);
  }

  const dx = Number(stroke?.shoulder_dx);
  const dxLo = Number(sig?.dx?.lo);
  const dxHi = Number(sig?.dx?.hi);
  if (
    Number.isFinite(dx) &&
    Number.isFinite(dxLo) &&
    Number.isFinite(dxHi) &&
    (dx < dxLo || dx > dxHi) &&
    !tags.includes(MISSING_TRUNK_ROTATION_TAG)
  ) {
    tags.push(MISSING_TRUNK_ROTATION_TAG);
  }
  return withTags(stroke, tags);
}

/**
 * Filter discovered Prep→Peak→Recovery cycles with a personal (or session) signature.
 * Survivors all count as the family stroke; topspin / bad_placement /
 * missing_trunk_rotation are tags.
 */
export function applyPlayerSignature(strokes, opts = {}) {
  const list = Array.isArray(strokes) ? strokes : [];
  const sessionBase = opts.sessionBase || null;
  const sessionTopspin = opts.sessionTopspin || null;
  const clipKind = String(opts.clipKind || "");
  const clusters = opts.clusters || simpleClusters(list);
  const clipSig = fitPlayerStrokeSignature(list, {
    clusters,
    calibratedFrom: sessionBase ? "warmup" : "clip",
  });
  const sig = sessionBase || clipSig;
  const calibratedFrom = sessionBase
    ? "warmup"
    : clipSig
      ? "clip"
      : "none";

  let kept = list;
  if (sig) {
    kept = filterByIsiAndTiming(list, sig, { clusters });
  }

  const gate = topspinGateFromThresholds(sessionBase, sessionTopspin);
  // Topspin warmup clips: always family-tag plain survivors (even when a prior
  // FH/BH warmup already filled sessionBase — otherwise only gate-tagged
  // topspins count and the volume stays far below the real stroke total).
  let warmupFamilyTag = null;
  if (clipKind === "forehand_topspin") warmupFamilyTag = "forehand";
  else if (clipKind === "backhand_topspin") warmupFamilyTag = "backhand";
  const tagged = kept.map((s) =>
    tagStroke(s, sig, sessionBase || sessionTopspin ? gate : null, {
      warmupFamilyTag,
    })
  );

  const peakMode = sig?.peak
    ? { n: sig.peak.n, med: sig.peak.med, mad: sig.peak.mad, lo: sig.peak.lo, hi: sig.peak.hi }
    : null;

  const player_signature = sig
    ? {
        ...sig,
        calibrated_from: calibratedFrom,
        topspin_gate: sessionBase || sessionTopspin ? gate : null,
      }
    : null;

  const player_thresholds = {
    source: calibratedFrom,
    n: sig?.n || tagged.length,
    modes: peakMode ? [peakMode] : [],
    applied_mode: peakMode,
    prep_angle: sig?.prep_angle || null,
    dx: sig?.dx || null,
    amplitude: sig?.amplitude || null,
    phase_ms: sig?.phase_ms || null,
    isi_ms: sig?.isi_ms || null,
    calibrated_from: calibratedFrom,
    topspin_gate: player_signature?.topspin_gate || null,
  };

  return {
    strokes: tagged,
    player_signature,
    player_thresholds,
  };
}

export function taggedSubset(strokes, tag = "topspin") {
  return (Array.isArray(strokes) ? strokes : []).filter((s) => hasTag(s, tag));
}

/**
 * Strokes counted under the topspin detection during a topspin warmup.
 * On topspin warmup clips every surviving family stroke counts (plain FH/BH
 * stay tagged forehand/backhand; real cues keep topspin). Elsewhere only
 * topspin-tagged strokes count.
 */
export function topspinWarmupCounted(strokes, { clipKind = "" } = {}) {
  const list = Array.isArray(strokes) ? strokes : [];
  if (clipKind === "forehand_topspin" || clipKind === "backhand_topspin") {
    return list;
  }
  return taggedSubset(list, "topspin");
}

export {
  BAD_PLACEMENT_ABS_DEG,
  BAND_PCT,
  MISSING_TRUNK_ROTATION_TAG,
  hasTag,
  median,
  robustBand,
  strokeTags,
};
